import React, { useState, useEffect, useRef } from 'react';
import { useAuth, createApiClient } from '../App';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  MapPin,
  Star,
  ForkKnife,
  SpinnerGap,
  ArrowsClockwise,
  Funnel,
  Eye,
  Heart,
  CalendarBlank,
  Camera,
  BookOpen,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Color schemes
const RATING_COLORS = {
  5: '#16a34a', 4: '#4B5945', 3: '#C4A35A', 2: '#ea580c', 1: '#A94438', 0: '#9A9590'
};

const CUISINE_COLORS = {
  'Italian': '#A94438', 'Japanese': '#C4A35A', 'French': '#4B5945',
  'Asian': '#e11d48', 'Indian': '#ea580c', 'Mediterranean': '#2563eb',
  'Seafood': '#0891b2', 'Steakhouse': '#7c2d12', 'Sushi': '#C4A35A',
  'Contemporary': '#7A7873', 'African': '#854d0e', 'Mexican': '#65a30d',
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VIBE_EMOJI = {
  romantic: '💕', celebration: '🥂', casual: '☀️',
  business: '💼', family: '👨‍👩‍👧', adventure: '🗺️',
};

// ── Memory-enriched Popup ─────────────────────────────────────────────
const MemoryPopup = ({ r, navigate }) => {
  const visits = r.recent_visits || [];
  const photos = (r.memory_photos || []).filter(Boolean);
  const favDishes = r.favourite_dishes || [];
  const latestSnippet = visits[0]?.journal_snippet;

  return (
    <div className="min-w-[240px] max-w-[300px]" data-testid={`popup-${r.id}`}>
      {/* Photo strip or hero */}
      {photos.length > 0 ? (
        <div className="flex gap-px -mt-1 -mx-1 mb-2 rounded-t-lg overflow-hidden" style={{ width: 'calc(100% + 8px)' }}>
          {photos.slice(0, 4).map((p, i) => (
            <div key={i} className="flex-1 h-16 min-w-0">
              <img src={`${BACKEND_URL}/api${p}`} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      ) : r.hero_image_url ? (
        <img
          src={r.hero_image_url} alt={r.name}
          className="w-full h-20 object-cover rounded-t-lg -mt-1 -mx-1 mb-2"
          style={{ width: 'calc(100% + 8px)' }}
        />
      ) : null}

      {/* Name + suburb */}
      <h3 className="font-semibold text-sm text-[#1A1918] mb-0.5 leading-tight">{r.name}</h3>
      {r.suburb && (
        <p className="text-[10px] text-[#7A7873] flex items-center gap-1 mb-1.5">
          <MapPin weight="fill" className="w-3 h-3" /> {r.suburb}
        </p>
      )}

      {/* Rating + visits + state */}
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        {r.aggregate_rating > 0 && (
          <span className="flex items-center gap-0.5 text-xs text-[#A94438]">
            <Star weight="fill" className="w-3 h-3" /> {r.aggregate_rating}
          </span>
        )}
        {r.visit_count > 0 && (
          <span className="text-[10px] text-[#7A7873]">{r.visit_count} visit{r.visit_count !== 1 ? 's' : ''}</span>
        )}
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
          r.state === 'visited' ? 'bg-[#D1E2C4] text-[#3E4A39]' :
          r.state === 'planned' ? 'bg-[#F5E6C8] text-[#8B6914]' :
          'bg-[#FDDDD8] text-[#A94438]'
        }`}>
          {r.state === 'want_to_try' ? 'Want to Try' : r.state === 'planned' ? 'Planned' : 'Visited'}
        </span>
      </div>

      {/* Mini timeline */}
      {visits.length > 0 && (
        <div className="border-t border-[#EAE8E3] pt-1.5 mt-1 mb-1.5">
          <p className="text-[9px] font-semibold text-[#9A9590] uppercase tracking-wider mb-1">Recent Visits</p>
          <div className="space-y-0.5">
            {visits.map((v, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[10px]">
                <span className="text-[#7A7873] w-[68px] flex-shrink-0">
                  {v.visited_at ? new Date(v.visited_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                </span>
                {v.overall_rating > 0 && (
                  <span className="flex items-center gap-0.5 text-[#C4A35A]">
                    <Star weight="fill" className="w-2.5 h-2.5" />{v.overall_rating}
                  </span>
                )}
                {v.meal_type && <span className="text-[#9A9590] capitalize">{v.meal_type}</span>}
                {v.vibe && <span className="text-[10px]" title={v.vibe}>{VIBE_EMOJI[v.vibe] || ''}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Journal snippet */}
      {latestSnippet && (
        <p className="text-[10px] text-[#4A4845] italic leading-snug line-clamp-2 mb-1.5 border-l-2 border-[#C4A35A] pl-2">
          "{latestSnippet}"
        </p>
      )}

      {/* Favourite dishes */}
      {favDishes.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {favDishes.slice(0, 4).map((d, i) => (
            <span key={i} className="text-[9px] bg-[#D1E2C4]/50 text-[#3E4A39] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
              <Heart weight="fill" className="w-2 h-2 text-[#A94438]" />{d}
            </span>
          ))}
        </div>
      )}

      {/* Cuisine tags */}
      {r.cuisine?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {r.cuisine.slice(0, 3).map((c, i) => (
            <span key={i} className="text-[10px] bg-[#F5F1EC] px-1.5 py-0.5 rounded">{c}</span>
          ))}
        </div>
      )}

      <button
        onClick={() => navigate(`/restaurants/${r.id}`)}
        className="text-xs text-[#A94438] font-medium hover:underline mt-0.5"
      >
        View details →
      </button>
    </div>
  );
};

const createColoredIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: 28px; height: 28px; border-radius: 50% 50% 50% 0;
      background: ${color}; transform: rotate(-45deg);
      border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      position: relative; top: -14px; left: -14px;
    "><div style="
      width: 10px; height: 10px; background: white; border-radius: 50%;
      position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    "></div></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};

// Component to fit map bounds to markers
const FitBounds = ({ restaurants }) => {
  const map = useMap();
  useEffect(() => {
    if (restaurants.length > 0) {
      const coords = restaurants.filter(r => r.lat && r.lng).map(r => [r.lat, r.lng]);
      if (coords.length > 0) {
        const bounds = L.latLngBounds(coords);
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      }
    }
  }, [restaurants, map]);
  return null;
};

const TasteMap = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const api = createApiClient(token);

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [colorMode, setColorMode] = useState('rating'); // 'rating' | 'cuisine' | 'state'
  const [filterState, setFilterState] = useState('all');

  useEffect(() => {
    fetchMapData();
  }, []);

  const fetchMapData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/map/restaurants-enriched');
      setRestaurants(res.data.restaurants || []);
    } catch (error) {
      toast.error('Failed to load map data');
    } finally {
      setLoading(false);
    }
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    try {
      const res = await api.post('/map/geocode');
      toast.success(`Geocoded ${res.data.geocoded} of ${res.data.total} restaurants`);
      fetchMapData();
    } catch (error) {
      toast.error('Geocoding failed');
    } finally {
      setGeocoding(false);
    }
  };

  const getMarkerColor = (r) => {
    if (colorMode === 'rating') {
      const rating = Math.round(r.aggregate_rating || 0);
      return RATING_COLORS[rating] || RATING_COLORS[0];
    } else if (colorMode === 'cuisine') {
      const primary = (r.cuisine || [])[0];
      return CUISINE_COLORS[primary] || '#7A7873';
    } else {
      return r.state === 'visited' ? '#4B5945' : r.state === 'planned' ? '#C4A35A' : '#A94438';
    }
  };

  const mappable = restaurants.filter(r => r.lat && r.lng);
  const unmapped = restaurants.filter(r => !r.lat || !r.lng);
  const filtered = filterState === 'all' ? mappable : mappable.filter(r => r.state === filterState);

  // Cape Town center
  const center = [-33.9249, 18.4241];

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 animate-spin text-[#A94438]" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col pb-20 md:pb-0" data-testid="taste-map-page">
      <SEO title="Taste Map" description="See your dining footprint across Cape Town on an interactive map with colour-coded pins by cuisine." path="/map" noindex />
      {/* Controls */}
      <div className="bg-white border-b border-[#EAE8E3] px-4 py-3 flex flex-wrap items-center gap-2 sm:gap-4">
        <h1 className="font-heading text-lg sm:text-xl font-semibold text-[#1A1918] flex items-center gap-2">
          <MapPin weight="duotone" className="w-5 h-5 text-[#A94438]" />
          Taste Map
        </h1>

        <div className="flex-1" />

        {/* Color Mode Toggle */}
        <div className="flex bg-[#F5F1EC] rounded-lg p-0.5">
          {[
            { id: 'rating', label: 'Rating', icon: Star },
            { id: 'cuisine', label: 'Cuisine', icon: ForkKnife },
            { id: 'state', label: 'Status', icon: Eye },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setColorMode(id)}
              className={`px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
                colorMode === id ? 'bg-white text-[#1A1918] shadow-sm' : 'text-[#7A7873]'
              }`}
              data-testid={`color-mode-${id}`}
            >
              <Icon weight="bold" className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* State Filter */}
        <select
          value={filterState}
          onChange={(e) => setFilterState(e.target.value)}
          className="text-xs border border-[#E8DCD0] rounded-lg px-2 py-1.5 bg-white"
          data-testid="filter-state"
        >
          <option value="all">All ({mappable.length})</option>
          <option value="want_to_try">Want to Try</option>
          <option value="planned">Planned</option>
          <option value="visited">Visited</option>
        </select>

        {/* Geocode Button */}
        {unmapped.length > 0 && (
          <Button
            onClick={handleGeocode}
            disabled={geocoding}
            size="sm"
            variant="outline"
            className="text-xs"
            data-testid="geocode-btn"
          >
            {geocoding ? (
              <SpinnerGap className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <ArrowsClockwise weight="bold" className="w-4 h-4 mr-1" />
            )}
            Map {unmapped.length} more
          </Button>
        )}
      </div>

      {/* Map */}
      <div className="flex-1 relative" data-testid="map-container">
        {filtered.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#F9F8F5] z-10">
            <div className="text-center">
              <MapPin weight="duotone" className="w-12 h-12 mx-auto mb-3 text-[#E8DCD0]" />
              <p className="text-[#7A7873] text-sm mb-3">
                {restaurants.length === 0
                  ? 'No restaurants yet. Add some from your Dashboard!'
                  : unmapped.length > 0
                    ? `${unmapped.length} restaurants need geocoding`
                    : 'No restaurants match this filter'}
              </p>
              {unmapped.length > 0 && (
                <Button onClick={handleGeocode} disabled={geocoding} size="sm" className="btn-primary">
                  {geocoding ? 'Geocoding...' : `Geocode ${unmapped.length} restaurants`}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <MapContainer
            center={center}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds restaurants={filtered} />
            {filtered.map((r) => (
              <Marker
                key={r.id}
                position={[r.lat, r.lng]}
                icon={createColoredIcon(getMarkerColor(r))}
              >
                <Popup maxWidth={320}>
                  <MemoryPopup r={r} navigate={navigate} />
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-[#EAE8E3] max-w-[200px]" data-testid="map-legend">
          <h4 className="text-xs font-semibold text-[#1A1918] mb-2">
            {colorMode === 'rating' ? 'Rating' : colorMode === 'cuisine' ? 'Cuisine' : 'Status'}
          </h4>
          <div className="space-y-1">
            {colorMode === 'rating' && (
              <>
                {[5, 4, 3, 2, 1].map(r => (
                  <div key={r} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: RATING_COLORS[r] }} />
                    <span className="text-[10px] text-[#7A7873]">{r} star{r !== 1 ? 's' : ''}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: RATING_COLORS[0] }} />
                  <span className="text-[10px] text-[#7A7873]">Unrated</span>
                </div>
              </>
            )}
            {colorMode === 'cuisine' && (
              Object.entries(CUISINE_COLORS).slice(0, 8).map(([name, color]) => (
                <div key={name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: color }} />
                  <span className="text-[10px] text-[#7A7873]">{name}</span>
                </div>
              ))
            )}
            {colorMode === 'state' && (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: '#4B5945' }} />
                  <span className="text-[10px] text-[#7A7873]">Visited</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: '#C4A35A' }} />
                  <span className="text-[10px] text-[#7A7873]">Planned</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: '#A94438' }} />
                  <span className="text-[10px] text-[#7A7873]">Want to Try</span>
                </div>
              </>
            )}
          </div>
          <p className="text-[10px] text-[#9A9590] mt-2 border-t border-[#EAE8E3] pt-1">{filtered.length} pins</p>
        </div>
      </div>
    </div>
  );
};

export default TasteMap;
